// PrintReceipt.jsx
import QRCode from "qrcode";
import { showToast } from "../ToastMessage.jsx";

/**
 * Generates and opens a print window for a sale receipt
 * @param {Object} sale - Sale object containing items, totals, and metadata
 * @param {string} pharmacistId - ID of the pharmacist who made the sale
 */
export const printReceipt = async (sale, pharmacistId) => {
  if (!sale) {
    showToast({ type: "error", message: "No sale data to print" });
    return;
  }

  try {
    // Generate QR code as data URL
    const qrPayload = JSON.stringify({ 
      pharmacistId, 
      invoiceNo: sale.invoiceNo 
    });
    const qrCodeDataUrl = await QRCode.toDataURL(qrPayload, {
      width: 150,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    });

    const html = generatePrintHtml(sale, pharmacistId, qrCodeDataUrl);
    const printWindow = window.open("", "_blank", "width=600,height=800");
    
    if (!printWindow) {
      showToast({
        type: "error",
        message: "Popup blocked. Please allow popups for printing.",
      });
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 700);
  } catch (error) {
    console.error("Error generating QR code:", error);
    showToast({
      type: "error",
      message: "Failed to generate receipt",
    });
  }
};

/**
 * Generates HTML string for the receipt
 * @param {Object} sale - Sale object
 * @param {string} pharmacistId - Pharmacist ID
 * @param {string} qrCodeDataUrl - Base64 data URL of the QR code
 * @returns {string} HTML string for printing
 */
const generatePrintHtml = (sale, pharmacistId, qrCodeDataUrl) => {
  const date = new Date(sale.createdAt || Date.now()).toLocaleString();
  
  const rows = sale.items
    .map(
      (item) => `<tr>
        <td>${item.name}</td>
        <td class="text-end">${item.quantity}</td>
        <td class="text-end">${item.priceAtSale.toFixed(2)}</td>
        <td class="text-end">${(item.discount || 0).toFixed(2)}</td>
        <td class="text-end">${item.total.toFixed(2)}</td>
      </tr>`
    )
    .join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Payment Slip - Zainab Medical & Children Care Centre Pharmacy</title>
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet"/>
  <style>
    body {
      font-family: Arial, sans-serif;
      padding: 20px;
      color: #000;
    }
    .receipt {
      width: 360px;
      margin: 0 auto;
    }
    table {
      width: 100%;
    }
    .small {
      font-size: 12px;
      color: #666;
    }
    .hospital-name {
      font-size: 18px;
      font-weight: bold;
      color: #0066cc;
      margin-bottom: 4px;
    }
    .return-policy {
      background: #fff3cd;
      padding: 8px;
      margin: 12px 0;
      border: 1px solid #856404;
      border-radius: 4px;
      font-size: 11px;
      text-align: center;
      line-height: 1.4;
    }
    .return-policy .bold {
      font-weight: bold;
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
    }
    .developer-credit {
      text-align: center;
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1px solid #ccc;
      font-size: 9px;
      color: #555;
    }
    @media print {
      body {
        padding: 0;
      }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="text-center">
      <div class="hospital-name">Zainab Medical & Children Care Centre Pharmacy</div>
      <div class="small">Payment Slip</div>
      <div class="small">${date}</div>
    </div>
    
    <table class="table table-sm mt-3">
      <thead>
        <tr>
          <th>Name</th>
          <th class="text-end">Qty</th>
          <th class="text-end">Price</th>
          <th class="text-end">Disc</th>
          <th class="text-end">Total</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    
    <div class="text-end" style="width:300px;margin-left:auto">
      <div>Subtotal: <strong>${sale.subTotal.toFixed(2)}</strong></div>
      <div>Discount: <strong>${(sale.overallDiscount || 0).toFixed(2)}</strong></div>
      <div>Grand: <strong>${sale.grandTotal.toFixed(2)}</strong></div>
    </div>
    
    <div class="mt-3">
      <div>Sold by: ${sale.soldBy?.name || "-"}</div>
      <div class="small">Invoice: ${sale.invoiceNo}</div>
    </div>
    
    <!-- Return Policy Notice -->
    <div class="return-policy">
      <span class="bold">⚠️ RETURN POLICY</span>
      Fridge items are not returnable.<br/>
      No medicines are returned without Bill.
    </div>
    
    <div class="text-center mt-2 small">Thank you for choosing Zainab Medical & Children Care Centre Pharmacy</div>
    
    <!-- Developer Credit -->
    <div class="developer-credit">
      Computer software developed by Bilal Consultancy - Ph: +923152311183
    </div>
  </div>
</body>
</html>`;
};

export default printReceipt;